import { Injectable, signal, effect, OnDestroy } from '@angular/core';
import { createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { sepolia } from '@reown/appkit/networks';
import { getAccount, watchAccount, Config } from '@wagmi/core';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class Web3Service implements OnDestroy {

  private readonly projectId = environment.PROJECT_ID; 

  private wagmiAdapter!: WagmiAdapter;
  private appKitModal: any;
  private unwatchAccount?: () => void;

  // Signals for state management
  public isConnected = signal<boolean>(false);
  public userAddress = signal<string | null>(null);
  public chainId = signal<number | undefined>(undefined);

  constructor() {
    this.initAppKit();
  }

  private initAppKit() {
    // 1. Create Wagmi Adapter
    this.wagmiAdapter = new WagmiAdapter({
      projectId: this.projectId,
      networks: [sepolia],
      // Let AppKit manage config entirely
    });

    // 2. Create Modal
    this.appKitModal = createAppKit({
      adapters: [this.wagmiAdapter],
      networks: [sepolia],
      projectId: this.projectId,
      themeMode: 'dark',
      chainImages: {
        [sepolia.id]: '/loony-Token.png'
      },
      features: {
        analytics: false
      }
    });

    // 3. Setup watchers for wagmi core state
    this.setupWatchers();
  }

  private setupWatchers() {
    const config = this.getWagmiConfig();
    
    // Initial state
    const account = getAccount(config);
    this.updateState(account);

    // Watch for state changes
    this.unwatchAccount = watchAccount(config, {
      onChange: (newAccount) => {
        this.updateState(newAccount);
      }
    });
  }

  private updateState(account: any) {
    this.isConnected.set(account.isConnected);
    this.userAddress.set(account.address || null);
    this.chainId.set(account.chainId);
  }

  public getWagmiConfig(): Config {
    return this.wagmiAdapter.wagmiConfig as Config;
  }

  public async connect() {
    await this.appKitModal.open();
  }

  public async disconnect() {
    await this.appKitModal.disconnect();
  }

  ngOnDestroy() {
    if (this.unwatchAccount) {
      this.unwatchAccount();
    }
  }
}
